import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function InsuranceClaimPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const SITE_URL = 'https://mercedwaterrestoration.com';

    // Set page title
    document.title = 'Water Damage Insurance Claims Guide | Indoor-Restore';

    // Add meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      'content',
      'Learn how to navigate your homeowner\'s insurance claim for water damage in Merced, CA. Expert tips on documentation, adjuster meetings, and maximizing your payout from Indoor-Restore Services.'
    );

    // Add keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      'content',
      'water damage insurance claim, homeowners insurance water damage, insurance claim process, water damage coverage California, Merced insurance claims'
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/homeowners-insurance-claim-water-damage`);

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Navigating Your Homeowner's Insurance Claim for Water Damage",
      "description": "A comprehensive guide to filing and maximizing your water damage insurance claim in Merced, CA. Learn the step-by-step process from documentation to payout.",
      "image": "https://public.readdy.ai/ai/img_res/2cc6ae8cc15ef23259e4a85102b8abbe.jpg",
      "datePublished": "2025-05-14T08:00:00-07:00",
      "dateModified": "2025-05-14T08:00:00-07:00",
      "author": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "logo": {
          "@type": "ImageObject",
          "url": "https://mercedwaterrestoration.com/wp-content/uploads/2024/11/Indoor-Restore-Services-Logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${SITE_URL}/blog/homeowners-insurance-claim-water-damage`
      }
    });
    document.head.appendChild(script);

    // Add FAQPage Schema
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Does homeowner's insurance cover water damage in California?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most homeowner's insurance policies in California cover sudden and accidental water damage, such as burst pipes, appliance malfunctions, or roof leaks from storms. However, flood damage from external sources (rivers, heavy rain) typically requires separate flood insurance. Gradual damage from neglect or lack of maintenance is usually not covered."
          }
        },
        {
          "@type": "Question",
          "name": "How long does a water damage insurance claim take in Merced?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "In Merced, CA, most water damage insurance claims are processed within 30–60 days, depending on the complexity and extent of damage. Simple claims with clear documentation can be resolved faster, while claims involving structural damage or disputes may take longer. Working with a professional restoration company like Indoor-Restore Services can help expedite the process."
          }
        },
        {
          "@type": "Question",
          "name": "What should I document for my water damage insurance claim?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Document everything: take photos and videos of all damaged areas, affected belongings, and the water source. Keep receipts for emergency repairs, temporary housing, and damaged items. Create a detailed inventory list with estimated values. Save all communication with your insurance company and restoration contractors. The more documentation you have, the stronger your claim."
          }
        },
        {
          "@type": "Question",
          "name": "Should I hire a public adjuster for my water damage claim?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A public adjuster can be helpful for large or complex water damage claims, especially if your initial settlement offer seems too low. They work for you (not the insurance company) and typically charge 5–15% of your final payout. For straightforward claims with good documentation and a reputable restoration company handling the work, you may not need one. Indoor-Restore Services can provide detailed estimates and documentation to support your claim."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100 pt-24 pb-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/blog" className="hover:text-teal-600 transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-gray-900 font-medium">Insurance Claims</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
              Insurance Tips
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Navigating Your Homeowner's Insurance Claim for Water Damage
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                May 14, 2025
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                6 min read
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line"></i>
                Merced, CA
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full h-96 rounded-xl overflow-hidden mb-10 shadow-lg">
            <img
              src="https://readdy.ai/api/search-image?query=insurance-agent-homeowner-reviewing-water-damage-claim-documents-kitchen-table-professional-setting-warm-lighting-realistic-photography&width=1200&height=630&seq=insurance-claim-hero-article&orientation=landscape"
              alt="Homeowner reviewing water damage insurance claim documents with insurance agent"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong>Dealing with water damage is stressful enough</strong> — but navigating the insurance claim process can feel overwhelming. Whether you're facing a burst pipe, roof leak, or appliance malfunction in your Merced home, understanding how to file and maximize your homeowner's insurance claim is critical to getting the compensation you deserve.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              At <strong>Indoor-Restore Services</strong>, we've helped hundreds of Merced homeowners through the insurance claim process for water damage restoration. In this comprehensive guide, we'll walk you through every step — from initial documentation to final payout — so you can avoid common mistakes and get your home restored quickly.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-shield-check-line text-teal-600"></i>
              Does Homeowner's Insurance Cover Water Damage?
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              Most standard homeowner's insurance policies in California cover <strong>sudden and accidental water damage</strong>. This includes:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Burst or frozen pipes</strong> inside your home</li>
              <li><strong>Appliance malfunctions</strong> (washing machine overflow, water heater failure)</li>
              <li><strong>Roof leaks</strong> caused by storm damage or fallen trees</li>
              <li><strong>Accidental overflow</strong> from sinks, tubs, or toilets</li>
              <li><strong>HVAC system leaks</strong> or condensation issues</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              However, there are important exclusions. <strong>Flood damage from external sources</strong> — such as rivers, heavy rain, or storm surge — typically requires separate flood insurance through the National Flood Insurance Program (NFIP). Additionally, <strong>gradual damage from neglect or lack of maintenance</strong> (like a slow leak you ignored for months) is usually not covered.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Pro Tip:</strong> Review your policy's water damage coverage limits and exclusions <em>before</em> disaster strikes. Many Merced homeowners are surprised to learn their policy has sub-limits for certain types of water damage.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-file-list-3-line text-teal-600"></i>
              Step 1: Document Everything Immediately
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              The moment you discover water damage, <strong>start documenting</strong>. Your insurance claim's success depends heavily on the quality of your evidence. Here's what to do:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Take photos and videos</strong> of all affected areas from multiple angles</li>
              <li><strong>Capture the water source</strong> (burst pipe, leaking appliance, roof damage)</li>
              <li><strong>Document damaged belongings</strong> — furniture, electronics, flooring, walls, personal items</li>
              <li><strong>Note the date and time</strong> you discovered the damage</li>
              <li><strong>Keep receipts</strong> for emergency repairs, temporary housing, and any immediate expenses</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              Don't throw away damaged items until your insurance adjuster has inspected them. Create a detailed inventory list with estimated replacement values. The more thorough your documentation, the stronger your claim.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-phone-line text-teal-600"></i>
              Step 2: Call Your Insurance Company Right Away
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Contact your insurance company within 24 hours</strong> of discovering water damage. Most policies require prompt notification, and delays can jeopardize your claim. When you call, have your policy number ready and provide a clear, factual description of what happened.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Your insurer will assign a claims adjuster to your case. Ask for their name, contact information, and claim number. Keep detailed notes of every conversation, including dates, times, and what was discussed.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Important:</strong> While you wait for the adjuster, you're required to <strong>mitigate further damage</strong>. This means stopping the water source if possible, removing standing water, and protecting undamaged areas. Save all receipts — these emergency mitigation costs are typically covered.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-team-line text-teal-600"></i>
              Step 3: Work with a Professional Restoration Company
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Hiring a reputable water damage restoration company like <strong>Indoor-Restore Services</strong> can make or break your insurance claim. Here's why:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>We provide detailed estimates</strong> that insurance adjusters trust and accept</li>
              <li><strong>We document the full scope of damage</strong> using industry-standard moisture meters and thermal imaging</li>
              <li><strong>We communicate directly with your insurance company</strong> to streamline the process</li>
              <li><strong>We prevent secondary damage</strong> (like mold growth) that could complicate your claim</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              Many Merced homeowners try to handle water damage themselves to save money — but this often backfires. Insurance companies may deny claims if proper drying and restoration protocols aren't followed. Professional restoration ensures your claim is supported by industry-standard documentation.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-search-eye-line text-teal-600"></i>
              Step 4: Meet with the Insurance Adjuster
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Your insurance adjuster will schedule an inspection of your property. <strong>Be present during this visit</strong> and bring your documentation. Walk them through the damage, point out all affected areas, and provide your inventory list.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              If you've hired a restoration company, have them present during the adjuster's visit. Our team at Indoor-Restore Services often accompanies homeowners during adjuster inspections to ensure nothing is overlooked.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Common adjuster tactics to watch for:</strong> Some adjusters may downplay damage or suggest lower-cost repairs. Don't be pressured into accepting an initial settlement offer if it doesn't cover the full restoration. You have the right to negotiate and provide additional evidence.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-error-warning-line text-teal-600"></i>
              Step 5: Avoid These Common Claim Mistakes
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              Many Merced homeowners unknowingly sabotage their own claims. Here are the top mistakes to avoid:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Delaying notification</strong> — Call your insurer immediately, even if damage seems minor</li>
              <li><strong>Throwing away evidence</strong> — Keep damaged items until the adjuster inspects them</li>
              <li><strong>Accepting the first offer</strong> — Initial settlements are often lower than what you're entitled to</li>
              <li><strong>Not reading your policy</strong> — Understand your coverage limits, deductibles, and exclusions</li>
              <li><strong>Skipping professional restoration</strong> — DIY repairs can void your claim or lead to denied coverage</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-money-dollar-circle-line text-teal-600"></i>
              Step 6: Maximize Your Payout
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              To get the maximum payout from your water damage claim, follow these expert tips:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Get multiple estimates</strong> from licensed restoration contractors</li>
              <li><strong>Include all related costs</strong> — temporary housing, storage, cleaning, mold prevention</li>
              <li><strong>Document hidden damage</strong> — Water often affects areas behind walls, under floors, and in ceilings</li>
              <li><strong>Consider hiring a public adjuster</strong> if your claim is large or disputed (they typically charge 5–15% of your payout)</li>
              <li><strong>Appeal if necessary</strong> — If your claim is denied or underpaid, you have the right to appeal with additional evidence</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              At Indoor-Restore Services, we provide comprehensive estimates that account for all necessary restoration work — not just surface-level repairs. Our detailed documentation helps ensure you receive fair compensation.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-time-line text-teal-600"></i>
              How Long Does the Claim Process Take?
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              In Merced, CA, most water damage insurance claims are resolved within <strong>30–60 days</strong>. Simple claims with clear documentation and no disputes can be processed faster. Complex claims involving structural damage, mold remediation, or disagreements over coverage may take longer.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Working with a professional restoration company can significantly speed up the process. We provide insurance-ready documentation, communicate directly with adjusters, and ensure all necessary repairs are completed to code.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-question-line text-teal-600"></i>
              Frequently Asked Questions
            </h2>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Does homeowner's insurance cover water damage in California?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Most homeowner's insurance policies in California cover sudden and accidental water damage, such as burst pipes, appliance malfunctions, or roof leaks from storms. However, flood damage from external sources (rivers, heavy rain) typically requires separate flood insurance. Gradual damage from neglect or lack of maintenance is usually not covered.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How long does a water damage insurance claim take in Merced?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                In Merced, CA, most water damage insurance claims are processed within 30–60 days, depending on the complexity and extent of damage. Simple claims with clear documentation can be resolved faster, while claims involving structural damage or disputes may take longer. Working with a professional restoration company like Indoor-Restore Services can help expedite the process.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">What should I document for my water damage insurance claim?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Document everything: take photos and videos of all damaged areas, affected belongings, and the water source. Keep receipts for emergency repairs, temporary housing, and damaged items. Create a detailed inventory list with estimated values. Save all communication with your insurance company and restoration contractors. The more documentation you have, the stronger your claim.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Should I hire a public adjuster for my water damage claim?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                A public adjuster can be helpful for large or complex water damage claims, especially if your initial settlement offer seems too low. They work for you (not the insurance company) and typically charge 5–15% of your final payout. For straightforward claims with good documentation and a reputable restoration company handling the work, you may not need one. Indoor-Restore Services can provide detailed estimates and documentation to support your claim.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-shield-star-line text-teal-600"></i>
              Final Thoughts: Get Expert Help with Your Claim
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Navigating a homeowner's insurance claim for water damage doesn't have to be overwhelming. By documenting thoroughly, communicating promptly with your insurer, and working with a trusted restoration company, you can maximize your payout and get your Merced home back to normal quickly.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              At <strong>Indoor-Restore Services</strong>, we've helped countless Merced homeowners through the insurance claim process. Our team provides detailed estimates, industry-standard documentation, and direct communication with insurance adjusters to ensure you receive fair compensation.
            </p>

            <p className="text-gray-700 leading-relaxed">
              <strong>Dealing with water damage right now?</strong> Don't wait — call our 24/7 emergency line at <a href="tel:2094512288" className="text-teal-600 font-semibold hover:text-teal-700 underline">(209) 451-2288</a> for immediate assistance. We'll help you document the damage, start the restoration process, and work with your insurance company to get you the coverage you deserve.
            </p>
          </div>
        </div>
      </article>

      {/* Emergency CTA */}
      <section className="bg-teal-700 py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Need Help with Your Water Damage Claim?</h3>
          <p className="text-teal-100 mb-6 text-sm">
            Our Merced restoration experts work directly with insurance companies to ensure you get maximum coverage.
          </p>
          <a
            href="tel:2094512288"
            className="inline-flex items-center gap-2 bg-white text-teal-700 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            Call 24/7: (209) 451-2288
          </a>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Related Articles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Link
              to="/blog/water-damage-first-24-hours"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
            >
              <div className="w-full h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=flooded%20living%20room%20interior%20with%20water%20on%20hardwood%20floor%20furniture%20damaged%20homeowner%20assessing%20water%20damage%20natural%20light%20realistic%20photography&width=400&height=250&seq=blog1-indoor&orientation=landscape"
                  alt="What to Do in the First 24 Hours After Water Damage"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  Water Damage
                </span>
                <h4 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-teal-700 transition-colors">
                  What to Do in the First 24 Hours After Water Damage
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                  Acting fast after water damage is critical. Learn the immediate steps you should take to minimize damage.
                </p>
              </div>
            </Link>

            <Link
              to="/blog/prevent-mold-after-flood"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
            >
              <div className="w-full h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20mold%20remediation%20specialist%20in%20protective%20gear%20inspecting%20wall%20with%20moisture%20meter%20in%20residential%20home%20clean%20bright%20interior&width=400&height=250&seq=blog2-indoor&orientation=landscape"
                  alt="How to Prevent Mold After a Flood"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  Mold Prevention
                </span>
                <h4 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-teal-700 transition-colors">
                  How to Prevent Mold After a Flood: A Complete Guide
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                  Mold can begin growing within 24–48 hours. Discover proven strategies to stop mold before it starts.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}